import React from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { GoogleAdsCampaignName } from 'src/api/queries';

const PREVIEW_LIST_LIMIT = 20;

type CampaignPreviewProps = {
  isLoading: boolean;
  isError: boolean;
  campaigns: GoogleAdsCampaignName[] | undefined;
  matchingCampaigns: GoogleAdsCampaignName[] | null;
  filter: string | null;
};

export const CampaignPreview: React.FC<CampaignPreviewProps> = ({
  isLoading,
  isError,
  campaigns,
  matchingCampaigns,
  filter,
}) => {
  if (isLoading) {
    return (
      <Flex align="center" gap={2} mt={3}>
        <Spinner size="xs" color="gray.400" />
        <Text fontSize="sm" color="gray.500" fontFamily="marketingPage">
          Loading campaigns…
        </Text>
      </Flex>
    );
  }

  if (isError || !campaigns || !matchingCampaigns) {
    return (
      <Text mt={3} fontSize="sm" color="gray.400" fontFamily="marketingPage">
        Campaign preview unavailable.
      </Text>
    );
  }

  if (!filter) {
    return (
      <Text mt={3} fontSize="sm" color="gray.600" fontFamily="marketingPage">
        All {campaigns.length} campaigns will be imported.
      </Text>
    );
  }

  if (matchingCampaigns.length === 0) {
    return (
      <Text
        mt={3}
        fontSize="sm"
        color="background.red.500"
        fontFamily="marketingPage">
        No campaigns match this prefix.
      </Text>
    );
  }

  const visibleCampaigns = matchingCampaigns.slice(0, PREVIEW_LIST_LIMIT);
  const hiddenCount = matchingCampaigns.length - visibleCampaigns.length;

  return (
    <Box mt={3}>
      <Text
        fontSize="sm"
        fontWeight="600"
        color="text.title"
        fontFamily="marketingPage">
        Matches {matchingCampaigns.length} of {campaigns.length} campaigns
      </Text>
      <Box
        mt={1}
        maxH="160px"
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        px={3}
        py={2}>
        {visibleCampaigns.map((campaign) => (
          <Text
            key={campaign.id}
            fontSize="sm"
            color="gray.600"
            fontFamily="marketingPage"
            noOfLines={1}>
            {campaign.name}
          </Text>
        ))}
        {hiddenCount > 0 && (
          <Text
            fontSize="sm"
            color="gray.400"
            fontFamily="marketingPage"
            mt={1}>
            +{hiddenCount} more
          </Text>
        )}
      </Box>
    </Box>
  );
};
